import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { useIsEditing, useRawProperty } from '../utils/hooks';
import { PATHS, useSiteFlags } from '../utils/site';
import { CAT_LABEL, GALLERY_CATS, galleryStore, photoTiles, placeTiles, type GalleryCat } from '../utils/gallery';
import type { Poi } from '../utils/places';
import { useStore } from '../utils/store';

const toursLinkDef: SlotGroupDefinition = {
  label: 'Virtual Tour Link',
  description: 'Shown only when the property has virtual tours.',
  fields: {
    label: { label: 'Link Text', type: 'text', defaultValue: 'Take a Virtual Tour' },
    url: { label: 'URL', type: 'url', defaultValue: '/virtual-tours' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'virtual_tour_clicked' },
  },
};

const gcat =
  'inline-flex h-11 items-center whitespace-nowrap rounded-full border border-solid border-[--q-border] bg-[--q-paper] px-[22px] text-[15px] font-medium leading-5 text-[--q-ink] [transition:background-color_var(--q-motion-fast),color_var(--q-motion-fast),border-color_var(--q-motion-fast)] [&_p]:m-0 hover:border-[--q-primary-on-paper] aria-pressed:border-primary aria-pressed:bg-primary aria-pressed:text-primary-foreground max-[767px]:shrink-0 max-[767px]:px-[18px] max-[767px]:text-[14px] min-[768px]:max-[1099px]:min-h-11';

/** Filter pills for the gallery mosaic (only the categories this property has photos for) and the virtual tour link. */
export function GalleryCategoryBar() {
  const p = useRawProperty();
  const flags = useSiteFlags();
  const editing = useIsEditing();
  const tours = useSlotGroup('gallerycategorybar.tours_link', toursLinkDef);
  const { cat } = useStore(galleryStore);

  const tiles = [...photoTiles((p.photo_gallery as []) || []), ...placeTiles((p.neighborhood_pois as Poi[]) || [])];
  const cats = GALLERY_CATS.filter((c) => tiles.some((t) => t.cat === c));
  const showTours = flags.shows('virtual-tours');

  if (cats.length < 2 && !showTours && !editing) return null;

  const pick = (c: GalleryCat | 'all') => {
    galleryStore.set({ cat: c });
    history.replaceState(null, '', c === 'all' ? location.pathname + location.search : `#${c}`);
  };

  return (
    <section
      className={`flex min-h-[76px] items-center justify-between gap-x-6 gap-y-4 border-b border-b-[--q-border] bg-[--q-paper] px-[--gutter] max-[767px]:min-h-[68px] max-[767px]:flex-col max-[767px]:items-stretch max-[767px]:gap-2 max-[767px]:py-3${
        cats.length > 1 ? '' : ' max-[767px]:hidden'
      }`}
      id="gallery-photos"
      aria-label="Photo categories"
    >
      {cats.length > 1 ? (
        <div
          className="flex gap-2 max-[767px]:mx-[calc(-1*var(--gutter))] max-[767px]:overflow-x-auto max-[767px]:px-[--gutter] max-[767px]:[scrollbar-width:none] max-[767px]:[&::-webkit-scrollbar]:hidden"
          role="group"
          aria-label="Filter photos"
        >
          <button className={gcat} type="button" aria-pressed={cat === 'all'} onClick={() => pick('all')}>
            <RichTextContent path="gallerycategorybar.all_label" as="span" />
          </button>
          {cats.map((c) => (
            <button key={c} className={gcat} type="button" aria-pressed={cat === c} onClick={() => pick(c)}>
              {CAT_LABEL[c]}
            </button>
          ))}
        </div>
      ) : (
        <span />
      )}
      <EditableSlotGroup groupPath="gallerycategorybar.tours_link" definition={toursLinkDef}>
        {showTours && (
          <a
            className="textlink whitespace-nowrap [&.textlink]:text-[15px] max-[767px]:self-start max-[767px]:[&.textlink]:hidden"
            href={tours.url || PATHS.virtualTours}
            target={tours._target || '_self'}
            data-vibesite-event={tours.vibesite_event || undefined}
          >
            {tours.label || 'Take a Virtual Tour'} <span className="btn__arrow">→</span>
          </a>
        )}
      </EditableSlotGroup>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryCategoryBar',
  displayName: 'Gallery Filters',
  tokens: {
    'gallerycategorybar.all_label': { label: 'All Pill Text', type: 'richtext', defaultValue: '<p>All</p>' },
  },
  slotGroups: {
    'gallerycategorybar.tours_link': toursLinkDef,
  },
};

(GalleryCategoryBar as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default GalleryCategoryBar;
